import React, { useEffect, useState } from 'react'

import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow
} from '@coreui/react-pro'

import FormFileUpload from 'src/components/form/general-input-form/FormFileUpload'
import FormFileThumbnail from 'src/components/form/general-input-form/FormFileThumbnail'
import CIcon from '@coreui/icons-react'
import { cilCloudDownload, cilDescription, cilSpreadsheet } from '@coreui/icons'

import Axios from 'src/helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

const AppModalImportExcel = ({
  showModal,
  setShowModal,
  modalTitle,
  templateUrl,
  previewUrl,
  defaultFileName = 'template_import.xlsx',
  handleSubmit
}) => {
  const { showError, showSuccess } = useToastMessage()
  const [selectedFile, setSelectedFile] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [previewData, setPreviewData] = useState(null)

  useEffect(() => {
    if (!showModal) {
      setSelectedFile(null)
      setIsImporting(false)
      setIsDownloadingTemplate(false)
      setIsPreviewing(false)
      setPreviewData(null)
    }
  }, [showModal])

  useEffect(() => {
    if (selectedFile) {
      handlePreview()
    }
  }, [selectedFile])

  const validateAndSetFile = (file) => {
    if (!file) {
      setSelectedFile(null)
      setPreviewData(null)
      return
    }

    const allowedExtensions = ['.xlsx', '.xls']
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()

    if (!allowedExtensions.includes(fileExtension)) {
      showError('Only .xlsx or .xls files are allowed')
      setSelectedFile(null)
      setPreviewData(null)
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      showError('File size exceeds 10 MB limit')
      setSelectedFile(null)
      setPreviewData(null)
      return
    }

    setSelectedFile(file)
    setPreviewData(null)
  }

  const handleFileInput = (e) => {
    const file = e?.target?.files?.[0] || null
    validateAndSetFile(file)
  }

  const handleFileOnDrop = (acceptedFiles = []) => {
    const file = acceptedFiles?.[0] || null
    validateAndSetFile(file)
  }

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true)
    try {
      const response = await Axios({
        method: 'get',
        url: templateUrl,
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      })

      const disposition = response.headers?.['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/)
      const filename = filenameMatch?.[1] || defaultFileName

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showSuccess('Template downloaded successfully')
    } catch (err) {
      console.error(err)
      showError('Failed to download template')
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const handlePreview = async () => {
    if (!selectedFile) {
      showError('Please select a file first')
      return
    }

    if (!previewUrl) {
      showError('Preview endpoint is not configured')
      return
    }

    setIsPreviewing(true)
    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await Axios({
        method: 'post',
        url: previewUrl,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })

      const value = response?.data?.value || null
      setPreviewData(value)
      showSuccess('Preview generated')
    } catch (err) {
      console.error(err)
      const errorMessage = err?.response?.data?.errors?.[0]?.reason || err?.response?.data?.message
      showError(errorMessage || 'Failed to generate preview')
      setPreviewData(null)
    } finally {
      setIsPreviewing(false)
    }
  }

  const handleImport = async () => {
    if (!selectedFile) return

    if (!previewData) {
      showError('Please preview data before import')
      return
    }

    setIsImporting(true)
    try {
      await handleSubmit(selectedFile)
      setShowModal(false)
    } catch (err) {
      setIsImporting(false)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setPreviewData(null)
  }

  const handleViewFile = (file) => {
    if (!(file instanceof File)) return
    const fileUrl = window.URL.createObjectURL(file)
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
    setTimeout(() => {
      window.URL.revokeObjectURL(fileUrl)
    }, 1000)
  }

  const selectedFiles = selectedFile ? [selectedFile] : []

  return (
    <CModal
      alignment='center'
      visible={showModal}
      onClose={() => setShowModal(false)}
      size='lg'
    >
      <CModalHeader closeButton className='border-0 pb-0 pt-4 px-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='d-flex align-items-center justify-content-center rounded-3'
            style={{
              width: '44px',
              height: '44px',
              backgroundColor: '#FFF3CD',
              color: '#D97706'
            }}
          >
            <CIcon icon={cilSpreadsheet} size='xl' />
          </div>
          <div>
            <h5 className='mb-0 fw-bold' style={{ color: '#111827' }}>
              {modalTitle || 'Import Data'}
            </h5>
            <small style={{ color: '#6B7280' }}>
              Upload file, preview data, then import.
            </small>
          </div>
        </div>
      </CModalHeader>

      <CModalBody className='px-4 py-3'>
        <div className='mb-4'>
          <label className='fw-semibold mb-1 d-block' style={{ color: '#374151', fontSize: '14px' }}>
            Download Template
          </label>
          <small className='d-block mb-2' style={{ color: '#6B7280', fontSize: '13px' }}>
            Use this template so data columns match the system format.
          </small>

          <div
            className='d-flex align-items-center justify-content-between rounded-3 p-3'
            style={{
              backgroundColor: '#FFFBEB',
              border: '1px solid #FDE68A'
            }}
          >
            <div className='d-flex align-items-center gap-3'>
              <div
                className='d-flex align-items-center justify-content-center rounded-2'
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#D1FAE5',
                  color: '#059669'
                }}
              >
                <CIcon icon={cilDescription} size='lg' />
              </div>
              <div>
                <div className='fw-semibold' style={{ color: '#1F2937', fontSize: '14px' }}>
                  {defaultFileName}
                </div>
                <small style={{ color: '#6B7280', fontSize: '12px' }}>
                  Excel Spreadsheet
                </small>
              </div>
            </div>

            <CButton
              color='light'
              size='sm'
              className='d-flex align-items-center gap-2 border shadow-sm'
              onClick={handleDownloadTemplate}
              disabled={isDownloadingTemplate}
              style={{
                backgroundColor: '#FFFFFF',
                color: '#374151',
                fontWeight: 600,
                borderRadius: '6px',
                padding: '6px 14px'
              }}
            >
              {isDownloadingTemplate ? (
                <>
                  <CSpinner size='sm' />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <CIcon icon={cilCloudDownload} size='sm' />
                  <span>Download Template</span>
                </>
              )}
            </CButton>
          </div>
        </div>

        <div className='mb-4'>
          {
            !selectedFile ? (
              <FormFileUpload
                name='import_file'
                colSize='12'
                handleFileInput={handleFileInput}
                handleFileOnDrop={handleFileOnDrop}
                inputLabel='Upload File'
                multiple={false}
                invalid={false}
                isSpesificImage={false}
                maxFiles={1}
                supportedFileTypes='.xlsx .xls'
                maxSize={10000000}
              />
            ) : (
              <FormFileThumbnail
                name='import_file'
                colSize='6'
                files={selectedFiles}
                handleConfirmDelete={handleRemoveFile}
                handleViewFile={handleViewFile}
              />
            )
          }
        </div>

        <div>
          <label className='fw-semibold mb-2 d-block' style={{ color: '#374151', fontSize: '14px' }}>
            Data Preview
          </label>

          {
            previewData?.rows?.length > 0 ? (
              <>
                <small className='d-block mb-2' style={{ color: '#6B7280', fontSize: '12px' }}>
                  Showing {previewData.previewed_rows} of {previewData.total_rows} rows from uploaded file.
                </small>
                <div className='border rounded-3 p-2' style={{ maxHeight: '260px', overflow: 'auto' }}>
                  <CTable small hover responsive className='mb-0'>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell scope='col' className='text-nowrap'>
                          Row
                        </CTableHeaderCell>
                        {
                          (previewData.headers || []).map((header, index) => (
                            <CTableHeaderCell key={`header-${index}`} scope='col' className='text-nowrap'>
                              {header || `Column ${index + 1}`}
                            </CTableHeaderCell>
                          ))
                        }
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {
                        previewData.rows.map((row) => (
                          <CTableRow key={`row-${row.row_number}`}>
                            <CTableDataCell className='text-nowrap fw-semibold'>
                              {row.row_number}
                            </CTableDataCell>
                            {
                              row.values.map((cell, index) => (
                                <CTableDataCell key={`cell-${row.row_number}-${index}`} className='text-nowrap'>
                                  {cell ? cell : '-'}
                                </CTableDataCell>
                              ))
                            }
                          </CTableRow>
                        ))
                      }
                    </CTableBody>
                  </CTable>
                </div>
              </>
            ) : (
              <div
                className='border rounded-3 d-flex align-items-center justify-content-center'
                style={{
                  minHeight: '88px',
                  backgroundColor: '#F9FAFB',
                  color: '#6B7280',
                  fontSize: '13px'
                }}
              >
                No preview data yet. Upload file and click Preview Data.
              </div>
            )
          }
        </div>
      </CModalBody>

      <CModalFooter className='border-0 pt-2 pb-4 px-4'>
        <CButton
          color='light'
          className='border'
          onClick={() => setShowModal(false)}
          disabled={isImporting || isPreviewing}
          style={{
            backgroundColor: '#FFFFFF',
            color: '#374151',
            fontWeight: 500,
            borderRadius: '6px',
            padding: '8px 20px'
          }}
        >
          Cancel
        </CButton>

        <CButton
          color='primary'
          onClick={handleImport}
          disabled={!selectedFile || isImporting || isPreviewing || !previewData}
          style={{
            backgroundColor: '#2563EB',
            borderColor: '#2563EB',
            color: '#FFFFFF',
            fontWeight: 600,
            borderRadius: '6px',
            padding: '8px 20px'
          }}
        >
          {isImporting ? (
            <>
              <CSpinner size='sm' className='me-2' />
              <span>Importing...</span>
            </>
          ) : (
            'Import Data'
          )}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default AppModalImportExcel
